/**
 * Cross-tab serialization for cart sync.
 *
 * TWO locks, not one:
 *   - `wp-cart-sync` is taken per operation, for short critical sections.
 *   - `wp-cart-sync-leader` is held for the lifetime of the polling tab.
 * Sharing one lock would let the poll leader starve every other tab's pushes.
 *
 * Every acquisition is bounded. navigator.locks has no default timeout, and a
 * holder suspended by mobile Safari never releases — an unbounded wait would
 * hang the other tabs silently and forever. Failing to acquire simply skips
 * that cycle, which is always safe.
 */

const OPERATION_LOCK = "wp-cart-sync";
const LEADER_LOCK = "wp-cart-sync-leader";
const ACQUIRE_TIMEOUT_MS = 5000;

interface LockManagerLike {
  request: (
    name: string,
    options: { signal?: AbortSignal; mode?: "exclusive" | "shared" },
    callback: () => Promise<unknown>,
  ) => Promise<unknown>;
}

function lockManager(): LockManagerLike | null {
  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  return locks && typeof locks.request === "function" ? locks : null;
}

/** In-page fallback so a single tab still serializes without Web Locks. */
let localChain: Promise<unknown> = Promise.resolve();

function runLocally<T>(task: () => Promise<T>): Promise<T> {
  const result = localChain.then(task, task);
  localChain = result.catch(() => undefined);
  return result;
}

/**
 * Outcome of a guarded critical section.
 *
 * "Did not run" and "ran and returned nothing" MUST stay distinguishable. A
 * caller that reads lock contention as success will act as though its work
 * landed — requestClear did exactly that, emptying the local cart while the
 * server never received the tombstone, which resurrects the cart on the next
 * pull. That is the bug this whole feature exists to remove.
 */
export type LockOutcome<T> =
  | { readonly ran: true; readonly value: T }
  | { readonly ran: false; readonly reason: "contended" | "failed" };

/** Run a critical section under the operation lock, reporting whether it ran. */
export async function runWithSyncLock<T>(
  task: () => Promise<T>,
): Promise<LockOutcome<T>> {
  const locks = lockManager();
  if (!locks) {
    try {
      return { ran: true, value: await runLocally(task) };
    } catch {
      return { ran: false, reason: "failed" };
    }
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ACQUIRE_TIMEOUT_MS);
  let entered = false;

  try {
    const result = await locks.request(
      OPERATION_LOCK,
      { signal: controller.signal, mode: "exclusive" },
      async () => {
        entered = true;
        return await task();
      },
    );
    return { ran: true, value: result as T };
  } catch {
    // Never acquired (AbortError) vs acquired and the task threw. The caller
    // needs to tell those apart.
    return { ran: false, reason: entered ? "failed" : "contended" };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Convenience wrapper for callers that genuinely do not care why the section
 * did not run (pulls, polls — the next trigger re-derives everything).
 * Anything that mutates a cart must use runWithSyncLock instead.
 */
export async function withSyncLock<T>(
  task: () => Promise<T>,
): Promise<T | null> {
  const outcome = await runWithSyncLock(task);
  return outcome.ran ? outcome.value : null;
}

let leaderHeld = false;

/**
 * Try to become the polling tab. Resolves true if this tab now holds
 * leadership, which it keeps until the page goes away.
 */
export function tryBecomePollLeader(): Promise<boolean> {
  const locks = lockManager();
  if (!locks) {
    // No Web Locks: every tab polls itself. Acceptable — the poll is cheap and
    // this only affects older Safari.
    leaderHeld = true;
    return Promise.resolve(true);
  }
  if (leaderHeld) return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    let settled = false;
    void locks
      .request(LEADER_LOCK, { mode: "exclusive" }, async () => {
        leaderHeld = true;
        if (!settled) {
          settled = true;
          resolve(true);
        }
        // Hold for the page's lifetime; released automatically on unload.
        await new Promise<void>(() => {
          /* never resolves */
        });
      })
      .catch(() => {
        if (!settled) {
          settled = true;
          resolve(false);
        }
      });

    // Another tab already leads — do not wait on it.
    setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(false);
      }
    }, 250);
  });
}

export function isPollLeader(): boolean {
  return leaderHeld;
}
